import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import type { OwnerState } from '../lib/types';

/**
 * Proben in einer Testlobby.
 *
 * Damit lässt sich ein ganzer Abend durchspielen, bevor jemand streamt: erfundene Kanäle, ein
 * erfundenes Publikum, das abstimmt, und Teams, die Felder belegen. So sieht man, wie Overlays und
 * Balken aussehen, wenn wirklich etwas los ist – und nicht erst vor dreihundert Zuschauern.
 *
 * Diese Ansicht erscheint nur in Testlobbys. In einer produktiven Lobby gibt es sie nicht, und
 * selbst wenn jemand die Adresse errät, lehnt der Server ab.
 */
export function Probe({ state }: { state: OwnerState }) {
  const [meldung, setMeldung] = useState<string | null>(null);
  const [läuft, setLäuft] = useState(false);
  const [stimmen, setStimmen] = useState(80);
  const [felder, setFelder] = useState(6);

  if (!state.lobby.isTest) return null;

  async function tun(was: () => Promise<unknown>, erfolg: (ergebnis: never) => string) {
    setLäuft(true);
    setMeldung(null);
    try {
      const ergebnis = await was();
      setMeldung(erfolg(ergebnis as never));
    } catch (err) {
      setMeldung(err instanceof ApiError ? err.message : 'Das hat nicht geklappt.');
    } finally {
      setLäuft(false);
    }
  }

  return (
    <section className="panel probe">
      <h2>Probe</h2>
      <p className="muted small">
        Dies ist eine Testlobby. Hier lässt sich ein Abend durchspielen, ohne dass jemand zusieht –
        mit erfundenen Kanälen und einem erfundenen Publikum. In einer echten Lobby gibt es das
        nicht.
      </p>

      <div className="row">
        <button
          type="button"
          disabled={läuft}
          onClick={() =>
            void tun(
              () => api.probeKanäle(state.lobby.id),
              (e: { angelegt: number }) =>
                e.angelegt === 0 ? 'Die Kanäle gab es schon.' : `${e.angelegt} Kanäle angelegt.`,
            )
          }
        >
          Probe-Kanäle anlegen
        </button>
        <button
          type="button"
          className="ghost gefahr"
          disabled={läuft}
          onClick={() => void tun(() => api.probeAufräumen(state.lobby.id), () => 'Aufgeräumt.')}
        >
          Aufräumen
        </button>
      </div>

      <div className="zeile-form">
        <input
          type="number"
          min={1}
          max={500}
          value={stimmen}
          aria-label="Zahl der Stimmen"
          onChange={(e) => setStimmen(Number(e.target.value))}
        />
        <button
          type="button"
          disabled={läuft}
          onClick={() =>
            void tun(
              () => api.probeStimmen(state.lobby.id, stimmen),
              (e: { abgegeben: number }) => `${e.abgegeben} Stimmen abgegeben.`,
            )
          }
        >
          Publikum abstimmen lassen
        </button>
      </div>

      <div className="zeile-form">
        <input
          type="number"
          min={1}
          max={100}
          value={felder}
          aria-label="Zahl der Felder"
          onChange={(e) => setFelder(Number(e.target.value))}
        />
        <button
          type="button"
          disabled={läuft}
          onClick={() =>
            void tun(
              () => api.probeFelder(state.lobby.id, felder),
              (e: { belegt: number }) =>
                e.belegt === 0
                  ? 'Nichts belegt – läuft die Runde?'
                  : `${e.belegt} Felder belegt.`,
            )
          }
        >
          Felder belegen lassen
        </button>
      </div>

      {meldung && <p className="muted small">{meldung}</p>}
    </section>
  );
}
